/**
 * MasteryRadar — hexagonal spider/radar chart of the 6 core scholar-athlete
 * attributes, computed from real exit tickets, worksheet scores and attendance.
 * Toggles between Current Term Mastery and Overall Career Rating.
 */
import React, { useEffect, useState } from 'react';
import { Hexagon, Loader2 } from 'lucide-react';
import api from '../services/api';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const SIZE = 300;
const CENTER = SIZE / 2;
const R = 110;

const point = (i, frac) => {
  const angle = (-90 + i * 60) * (Math.PI / 180);
  return [CENTER + R * frac * Math.cos(angle), CENTER + R * frac * Math.sin(angle)];
};
const labelPoint = (i) => {
  const angle = (-90 + i * 60) * (Math.PI / 180);
  return [CENTER + (R + 26) * Math.cos(angle), CENTER + (R + 26) * Math.sin(angle)];
};

const MasteryRadar = () => {
  const [scope, setScope] = useState('term');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/school/mastery-radar?scope=${scope}`, { headers: authHeaders() })
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [scope]);

  const vertices = data?.vertices || [];
  const poly = vertices.map((v, i) => point(i, Math.max(0.04, v.score / 100)).join(',')).join(' ');

  return (
    <div className="mb-6 border-2 border-emerald-500/30 p-5" data-testid="mastery-radar">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2"><Hexagon className="w-4 h-4 text-emerald-400" /><h3 className="text-sm font-black tracking-[0.2em] uppercase">Mastery Radar</h3></div>
        <div className="flex border-2 border-zinc-800" data-testid="radar-scope-toggle">
          <button onClick={() => setScope('term')} className={`px-3 py-1.5 text-[9px] font-black tracking-widest uppercase transition-colors ${scope === 'term' ? 'bg-emerald-500 text-black' : 'text-zinc-400'}`} data-testid="radar-term">Current Term</button>
          <button onClick={() => setScope('career')} className={`px-3 py-1.5 text-[9px] font-black tracking-widest uppercase transition-colors ${scope === 'career' ? 'bg-emerald-500 text-black' : 'text-zinc-400'}`} data-testid="radar-career">Career</button>
        </div>
      </div>

      {loading ? (
        <div className="h-64 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-400" /></div>
      ) : (
        <div className="flex flex-col lg:flex-row items-center gap-5">
          <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} className="shrink-0">
            {[0.25, 0.5, 0.75, 1].map((f, k) => (
              <polygon key={k} points={[0, 1, 2, 3, 4, 5].map((i) => point(i, f).join(',')).join(' ')} fill="none" stroke="#27272a" strokeWidth="1" />
            ))}
            {[0, 1, 2, 3, 4, 5].map((i) => { const [x, y] = point(i, 1); return <line key={i} x1={CENTER} y1={CENTER} x2={x} y2={y} stroke="#27272a" strokeWidth="1" />; })}
            <polygon points={poly} fill="rgba(16,185,129,0.25)" stroke="#10b981" strokeWidth="2" data-testid="radar-polygon" />
            {vertices.map((v, i) => { const [x, y] = point(i, Math.max(0.04, v.score / 100)); return <circle key={i} cx={x} cy={y} r="3.5" fill="#10b981" />; })}
            {vertices.map((v, i) => {
              const [x, y] = labelPoint(i);
              return <text key={i} x={x} y={y} textAnchor={x < CENTER - 10 ? 'end' : x > CENTER + 10 ? 'start' : 'middle'} fill="#a1a1aa" fontSize="8" fontWeight="700" style={{ textTransform: 'uppercase' }}>{v.score}</text>;
            })}
          </svg>
          <div className="grid grid-cols-1 gap-1.5 w-full">
            {vertices.map((v, i) => (
              <div key={i} className="flex items-center justify-between gap-3 border-b border-zinc-800 py-1.5" data-testid={`radar-vertex-${v.key}`}>
                <span className="text-[11px] text-zinc-300 font-medium">{v.label}</span>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="w-20 h-1.5 bg-zinc-800 rounded-full overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${v.score}%` }} /></div>
                  <span className="text-xs font-black text-white w-7 text-right">{v.score}</span>
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between mt-2 pt-2 border-t-2 border-emerald-500/40">
              <span className="text-[11px] font-black uppercase tracking-widest text-emerald-400">Overall Rating</span>
              <span className="text-lg font-black text-white" data-testid="radar-overall">{data?.overall_rating}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasteryRadar;
